import { useEffect, useState } from "react";
import FormItem from "./FormItem";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Grid2 as Grid, Alert } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import api from "../../api"
import MultiSelectDropdown from "../../shared/MultiSelectDropdown";
import Select from "../../shared/Select/Select";
import { connect, useDispatch } from "react-redux";
import {
  updateRegions,
  updateUserBrand,
} from "../../redux/reducers/userReducer";

const BrandSalesInfoSection = ({ formoptions, selectedBrand }) => {
  const dispatch = useDispatch();
  const [snackbarState, setSnackbarState] = useState({ open: false });
  const [productGroups, setProductGroups] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [isLoadingProductGroups, setIsLoadingProductGroups] = useState(true);
  const [countries, setCountries] = useState([]);
  const [priceRange, setPriceRange] = useState('');
  const [salesChannels, setSalesChannels] = useState('');
  const [retailTypes, setRetailTypes] = useState('');
  const { open } = snackbarState;

  useEffect(() => {
    const fetchProductGroups = () => {
      setIsLoadingProductGroups(true);
      api.getProductGroups((res) => {
        if (res.status === 200 && res.data) {
          const formattedProductGroups = res.data
            .map((group) => ({
              label: group.name,
              value: group.id
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setProductGroups(formattedProductGroups);
        }
        setIsLoadingProductGroups(false);
      });
    };

    fetchProductGroups();
  }, []);

  useEffect(() => {
    setRegionOptions(
      formoptions.regions.filter((r) => {
        for (var i in countries) {
          if (countries[i].label == r.country) return true
        }
        return false;
      }).map((r) => { return { value: r.id, label: r.name } })
    )
  }, [formoptions, formoptions.regions, countries]);

  const getMarketData = () => {
    return selectedBrand?.market || {};
  };

  const getRegionsData = () => {
    return selectedBrand?.regions?.map((r) => {
      return { value: r.region_id, label: r.region }
    }) || [];
  };

  const [regions, setRegions] = useState(getRegionsData());

  const [selectedProductCategories, setSelectedProductCategories] = useState(() => {
    const marketData = getMarketData();
    if (marketData?.product_categories) {
      return marketData.product_categories.map((c) => {
        return { label: c.name, value: c.productgroup_id }
      });
    }
    return [];
  });

  const [selectedTargetGroups, setSelectedTargetGroups] = useState(() => {
    const marketData = getMarketData();
    if (marketData?.target_groups) {
      return marketData.target_groups.map((c) => {
        return { label: c, value: c }
      });
    }
    return [];
  });

  useEffect(() => {
    let tempCountries = [];
    try {
      tempCountries = JSON.parse(selectedBrand.country).map((val) => { return { label: val, value: val } });
    } catch (error) {
      tempCountries = [];
    }
    setCountries(tempCountries);

    const marketData = getMarketData();
    const regionsData = getRegionsData();

    setRegions(regionsData);

    if (marketData?.product_categories) {
      setSelectedProductCategories(marketData.product_categories.map((c) => ({
        label: c.name,
        value: c.productgroup_id
      })));
    } else {
      setSelectedProductCategories([]);
    }

    if (marketData?.target_groups) {
      setSelectedTargetGroups(marketData.target_groups.map((c) => ({
        label: c,
        value: c
      })));
    } else {
      setSelectedTargetGroups([]);
    }

    setPriceRange(selectedBrand?.price_range ?? '')
    setSalesChannels(selectedBrand?.sales_channels ?? '')
    setRetailTypes(selectedBrand?.retail_types ?? '')
  }, [selectedBrand]);

  const handleSaveChanges = () => {
    const formattedRegions = regions.map((region) => ({
      value: region.value,
      label: region.label,
    }));

    const payload = {
      region_presences: formattedRegions.map((region) => region.value),
      product_categories: selectedProductCategories.map((cat) => cat.value),
      target_groups: selectedTargetGroups.map((group) => group.value),
      sales_channels: salesChannels,
      price_range: priceRange,
      retail_types: retailTypes,
      agent_rep_regions: selectedBrand.agent_rep_regions,
    };

    if (formattedRegions?.length > 0) {
      // remove agent_rep_regions included in the region_presences
      payload.agent_rep_regions = payload.agent_rep_regions
        .filter(item => !payload.region_presences.includes(item.region_id))
        .map(item => item.region_id);
    }

    handleSaveBrandSpecificMarket(payload);
  };

  const handleSaveBrandSpecificMarket = (payload) => {
    api.updateBrandMarket(
      selectedBrand.id, payload,
      (res) => {
        if (res.status === 200) {
          dispatch(updateRegions(res.data.regions));
          dispatch(updateUserBrand(res.data));
          openSuccessSnackbar();
        }
      }
    );
  };

  const openSuccessSnackbar = () => {
    setSnackbarState({ open: true });
  };

  const handleCloseSnackbar = () => {
    setSnackbarState({ open: false });
  };

  return (
    <>
      <div className="space-y-8">
        <FormItem label="Product Categories">
          <MultiSelectDropdown
            options={productGroups}
            value={selectedProductCategories}
            onChange={setSelectedProductCategories}
            disabled={isLoadingProductGroups}
            placeholder={isLoadingProductGroups ? "Loading product groups..." : "Select product categories"}
          />
        </FormItem>

        <h2 className="text-xl font-semibold">Target Market</h2>
        <FormItem label="Activity Regions / Regions of Operation">
          <MultiSelectDropdown
            options={regionOptions}
            onChange={setRegions}
            value={regions}
          />
        </FormItem>

        <FormItem label="Current Sales Channels">
          <Select
            value={salesChannels}
            onChange={(e) => { 
              setSalesChannels(e.target.value) 
            }}
          >
            <option value=""></option>
            <option value="Retailer">Retailer</option>
            <option value="B2B">B2B</option>
            <option value="Marketplaces">Marketplaces</option>
          </Select>
        </FormItem>
        
        <FormItem label="Average Wholesale Price Range">
          <Select
            value={priceRange}
            onChange={(e) => { 
              setPriceRange(e.target.value) 
            }}
          >
            <option value=""></option>
            <option value="€0-€100">€0-€100</option>
            <option value="€100-€500">€100-€500</option>
            <option value="€500+">€500+</option>
          </Select>
        </FormItem>

        <FormItem label="Target Retail Type">
          <Select
            value={retailTypes}
            onChange={(e) => { 
              setRetailTypes(e.target.value) 
            }}
          >
            <option value=""></option>
            <option value="Concept Stores">Concept Stores</option>
            <option value="Boutiques">Boutiques</option>
            <option value="Chains">Chains</option>
          </Select>
        </FormItem>

        <Grid container direction={"row"} className={"fullWidth"}>
          <div className={"flex-1"}></div>
          <ButtonPrimary onClick={handleSaveChanges}>Save Changes</ButtonPrimary>
        </Grid>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        message="Sales Info Details Saved"
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Sales Info Details Saved Successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

const mapStateToProps = (state) => ({
  formoptions: state.formoptions.options
});

export default connect(
  mapStateToProps,
  {
    updateRegions,
    updateUserBrand,
  }
)(BrandSalesInfoSection);
